import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SymbolToolbar from "../../components/SymbolToolbar";
import { MathPreview } from "../../components/MathText";
import { showError, showSuccess, showWarning } from "../../utils/sweetAlert";
import { authFetch, fetchSectionWithQuestionsAPI, firstError as firstApiError } from "../../utils/api";
import { MATCH_COLUMN_I_KEYS, matchLabel } from "../../utils/questionTypes";
import "./AddQuestionNew.css";
import "../../styles/MathJax.css";

function AddQuestionNew() {
  const navigate = useNavigate();
  const { seriesId, sectionId } = useParams();
  const textareaRefs = useRef({});
  const lastFocusedTextarea = useRef(null);

  const [currentLanguage, setCurrentLanguage] = useState("english");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sectionName, setSectionName] = useState("");
  // The section's target number of questions (more are allowed; the header warns)
  const [targetCount, setTargetCount] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [optionCount, setOptionCount] = useState(4); // Dynamic options: 4-6
  const [matchMappings, setMatchMappings] = useState({}); // For MATCH type: { P: 'A', Q: 'B', ... }

  const [formData, setFormData] = useState({
    questionType: "SINGLE_CORRECT",
    paragraphEng: "",
    paragraphHindi: "",
    questionEng: "",
    optionAEng: "",
    optionBEng: "",
    optionCEng: "",
    optionDEng: "",
    optionEEng: "",
    optionFEng: "",
    optionPEng: "",
    optionQEng: "",
    optionREng: "",
    optionSEng: "",
    optionTEng: "",
    optionUEng: "",
    explanationEng: "",
    questionHindi: "",
    optionAHindi: "",
    optionBHindi: "",
    optionCHindi: "",
    optionDHindi: "",
    optionEHindi: "",
    optionFHindi: "",
    optionPHindi: "",
    optionQHindi: "",
    optionRHindi: "",
    optionSHindi: "",
    optionTHindi: "",
    optionUHindi: "",
    explanationHindi: "",
    paragraphEngImage: null,
    paragraphHindiImage: null,
    paragraphEngImagePreview: null,
    paragraphHindiImagePreview: null,
    correctAnswer: "A",
    correctAnswers: [], // For MULTI_CORRECT
    numericalValue: "",
    numericalRanges: [],
    rangeAnswer: "", // For RANGE type
    marks: "4",
    negativeMarks: "1",
    topic_name: "",
    topic_name_hi: "",
    youtubeLink: "",
    questionImage: null,
    imagePreview: null,
    // Option images (English)
    optionAEngImage: null,
    optionBEngImage: null,
    optionCEngImage: null,
    optionDEngImage: null,
    optionEEngImage: null,
    optionFEngImage: null,
    optionPEngImage: null,
    optionQEngImage: null,
    optionREngImage: null,
    optionSEngImage: null,
    optionTEngImage: null,
    optionUEngImage: null,
    // Option images (Hindi)
    optionAHindiImage: null,
    optionBHindiImage: null,
    optionCHindiImage: null,
    optionDHindiImage: null,
    optionEHindiImage: null,
    optionFHindiImage: null,
    optionPHindiImage: null,
    optionQHindiImage: null,
    optionRHindiImage: null,
    optionSHindiImage: null,
    optionTHindiImage: null,
    optionUHindiImage: null,
    // Option image previews (English)
    optionAEngImagePreview: null,
    optionBEngImagePreview: null,
    optionCEngImagePreview: null,
    optionDEngImagePreview: null,
    optionEEngImagePreview: null,
    optionFEngImagePreview: null,
    optionPEngImagePreview: null,
    optionQEngImagePreview: null,
    optionREngImagePreview: null,
    optionSEngImagePreview: null,
    optionTEngImagePreview: null,
    optionUEngImagePreview: null,
    // Option image previews (Hindi)
    optionAHindiImagePreview: null,
    optionBHindiImagePreview: null,
    optionCHindiImagePreview: null,
    optionDHindiImagePreview: null,
    optionEHindiImagePreview: null,
    optionFHindiImagePreview: null,
    optionPHindiImagePreview: null,
    optionQHindiImagePreview: null,
    optionRHindiImagePreview: null,
    optionSHindiImagePreview: null,
    optionTHindiImagePreview: null,
    optionUHindiImagePreview: null,
  });

  const [errors, setErrors] = useState({});

  // Fetch section details and questions
  useEffect(() => {
    const fetchSectionData = async () => {
      try {
        setIsLoading(true);
        // The section with its questions (name, target count, questions so far)
        const response = await fetchSectionWithQuestionsAPI(sectionId);
        if (!response) return;
        if (!response.success || !response.data) {
          throw new Error(response.message || "Failed to load the section");
        }

        const section = response.data;
        setSectionName(section.name || "");
        setTargetCount(Number(section.total_questions) || 0);
        setQuestions(section.questions || []);
      } catch (error) {
        console.error("Error fetching section data:", error);
        showError(`Error fetching section data: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    if (sectionId) {
      fetchSectionData();
    }
  }, [sectionId]);

  // Clean up invalid matchMappings when optionCount changes for MATCH type
  useEffect(() => {
    if (formData.questionType === "MATCH") {
      const validColumnII = ['P', 'Q', 'R', 'S', 'T', 'U'].slice(0, optionCount);
      const validColumnI = ['A', 'B', 'C', 'D', 'E', 'F'].slice(0, optionCount);
      setMatchMappings((prev) => {
        const next = {};
        // Keep only mappings whose both ends still exist
        validColumnII.forEach((key) => {
          if (prev[key] && validColumnI.includes(prev[key])) next[key] = prev[key];
        });
        return Object.keys(next).length !== Object.keys(prev).length ? next : prev;
      });
    }
  }, [optionCount, formData.questionType]);

  // Keep the selected correct answer(s) within the visible options when options are removed
  useEffect(() => {
    const validKeys = ['A', 'B', 'C', 'D', 'E', 'F'].slice(0, optionCount);
    setFormData((prev) => {
      const correctAnswer = validKeys.includes(prev.correctAnswer) ? prev.correctAnswer : "A";
      const correctAnswers = prev.correctAnswers.filter((k) => validKeys.includes(k));
      if (correctAnswer === prev.correctAnswer && correctAnswers.length === prev.correctAnswers.length) {
        return prev;
      }
      return { ...prev, correctAnswer, correctAnswers };
    });
  }, [optionCount]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear matchMappings when changing question type away from or to MATCH
    if (name === "questionType") {
      if (formData.questionType === "MATCH" || value === "MATCH") {
        setMatchMappings({});
      }
    }
    
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          questionImage: file,
          imagePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({
      ...prev,
      questionImage: null,
      imagePreview: null,
    }));
  };

  // Handle option image upload (lang: 'en' | 'hi')
  const handleOptionImageUpload = (optionKey, lang, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 250 * 1024) {
        showError("Image size must be less than 250 KB.");
        e.target.value = "";
        return;
      }
      const suffix = lang === "en" ? "Eng" : "Hindi";
      const imageKey = `option${optionKey}${suffix}Image`;
      const previewKey = `option${optionKey}${suffix}ImagePreview`;
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          [imageKey]: file,
          [previewKey]: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove option image (lang: 'en' | 'hi')
  const removeOptionImage = (optionKey, lang) => {
    const suffix = lang === "en" ? "Eng" : "Hindi";
    setFormData((prev) => ({
      ...prev,
      [`option${optionKey}${suffix}Image`]: null,
      [`option${optionKey}${suffix}ImagePreview`]: null,
    }));
  };

  // Handle paragraph image upload
  const handleParagraphImageUpload = (languageKey, e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 250 * 1024) {
        showError("Image size must be less than 250 KB.");
        e.target.value = "";
        return;
      }
      const imageKey = languageKey === "en" ? "paragraphEngImage" : "paragraphHindiImage";
      const previewKey =
        languageKey === "en" ? "paragraphEngImagePreview" : "paragraphHindiImagePreview";

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          [imageKey]: file,
          [previewKey]: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove paragraph image
  const removeParagraphImage = (languageKey) => {
    const imageKey = languageKey === "en" ? "paragraphEngImage" : "paragraphHindiImage";
    const previewKey =
      languageKey === "en" ? "paragraphEngImagePreview" : "paragraphHindiImagePreview";

    setFormData((prev) => ({
      ...prev,
      [imageKey]: null,
      [previewKey]: null,
    }));
  };

  // Wrap selected text with math delimiters
  const wrapWithMath = (type) => {
    let targetElement = document.activeElement;
    if (!targetElement || targetElement.tagName !== "TEXTAREA") {
      targetElement = lastFocusedTextarea.current;
    }

    if (targetElement && targetElement.tagName === "TEXTAREA") {
      const start = targetElement.selectionStart;
      const end = targetElement.selectionEnd;
      const fieldName = targetElement.name;
      const currentValue = formData[fieldName] || "";
      const selectedText = currentValue.substring(start, end);

      if (!selectedText) {
        showWarning(
          "Please select some text first to wrap with math delimiters."
        );
        return;
      }

      let wrappedText;
      if (type === "inline") {
        wrappedText = `\\(${selectedText}\\)`;
      } else if (type === "display") {
        wrappedText = `$$${selectedText}$$`;
      }

      const newValue =
        currentValue.substring(0, start) +
        wrappedText +
        currentValue.substring(end);

      setFormData({
        ...formData,
        [fieldName]: newValue,
      });

      // Set cursor position after the wrapped text
      setTimeout(() => {
        targetElement.focus();
        const cursorPos = start + wrappedText.length;
        targetElement.setSelectionRange(cursorPos, cursorPos);
      }, 10);
    } else {
      showWarning("Please click in a text field first.");
    }
  };

  // Insert equation symbol at cursor position
  const insertEquation = (latex) => {
    // Try to get the currently focused element first, otherwise use the last focused textarea
    let targetElement = document.activeElement;
    if (!targetElement || targetElement.tagName !== "TEXTAREA") {
      targetElement = lastFocusedTextarea.current;
    }

    if (targetElement && targetElement.tagName === "TEXTAREA") {
      const start = targetElement.selectionStart;
      const end = targetElement.selectionEnd;
      const fieldName = targetElement.name;
      const currentValue = formData[fieldName] || "";

      // Insert latex without wrapping - user can wrap later with wrapper buttons
      const newValue =
        currentValue.substring(0, start) + latex + currentValue.substring(end);

      setFormData({
        ...formData,
        [fieldName]: newValue,
      });

      // Set cursor position after inserted equation
      setTimeout(() => {
        targetElement.focus();
        const cursorPos = start + latex.length;
        targetElement.setSelectionRange(cursorPos, cursorPos);
      }, 10);
    } else {
      showWarning("Please click in a text field first, then select a symbol.");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // English question validation
    if (!formData.questionEng.trim()) {
      newErrors.questionEng = "English question text is required";
    }

    // Marks validation
    if (!formData.marks || parseFloat(formData.marks) <= 0) {
      newErrors.marks = "Positive marks is required";
    }

    // Question type specific validation
    if (formData.questionType === "SINGLE_CORRECT" || formData.questionType === "MULTI_CORRECT" || formData.questionType === "PARAGRAPH") {
      // Validate paragraph for PARAGRAPH type
      if (formData.questionType === "PARAGRAPH" && !formData.paragraphEng.trim()) {
        newErrors.paragraphEng = "English paragraph/comprehension text is required";
      }

      // Validate options for MCQ and PARAGRAPH types (dynamic based on optionCount)
      const optionKeys = ['A', 'B', 'C', 'D', 'E', 'F'];
      for (let i = 0; i < optionCount; i++) {
        const key = optionKeys[i];
        const fieldName = `option${key}Eng`;
        if (!formData[fieldName].trim()) {
          newErrors[fieldName] = `English Option ${key} is required`;
        }
      }

      // Hindi options are now optional - no validation needed

      // Correct answer validation
      if (formData.questionType === "SINGLE_CORRECT" && !formData.correctAnswer) {
        newErrors.correctAnswer = "Please select a correct answer";
      }
      if (formData.questionType === "MULTI_CORRECT" && formData.correctAnswers.length === 0) {
        newErrors.correctAnswers = "Please select at least one correct answer";
      }
      if (formData.questionType === "PARAGRAPH" && !formData.correctAnswer) {
        newErrors.correctAnswer = "Please select a correct answer";
      }
    } else if (formData.questionType === "MATCH") {
      // Validate Column I options (A, B, C, D, E, F)
      const columnIKeys = ['A', 'B', 'C', 'D', 'E', 'F'];
      for (let i = 0; i < optionCount; i++) {
        const key = columnIKeys[i];
        const fieldName = `option${key}Eng`;
        if (!formData[fieldName].trim()) {
          newErrors[fieldName] = `English Column I Item ${matchLabel(key)} is required`;
        }
      }

      // Validate Column II options (P, Q, R, S, T, U)
      const columnIIKeys = ['P', 'Q', 'R', 'S', 'T', 'U'];
      for (let i = 0; i < optionCount; i++) {
        const key = columnIIKeys[i];
        const fieldName = `option${key}Eng`;
        if (!formData[fieldName].trim()) {
          newErrors[fieldName] = `English Column II Item ${matchLabel(key)} is required`;
        }
      }

      // Validate match mappings
      const requiredColumnIIKeys = columnIIKeys.slice(0, optionCount);
      for (const key of requiredColumnIIKeys) {
        if (!matchMappings[key]) {
          newErrors.matchMappings = "Please map all Column II items to Column I items";
          break;
        }
      }
    } else if (formData.questionType === "NUMERICAL") {
      // Validate numerical value
      if (!formData.numericalValue || formData.numericalValue.trim() === "") {
        newErrors.numericalValue = "Numerical value is required";
      }
    } else if (formData.questionType === "RANGE") {
      // Validate range answer
      if (!formData.rangeAnswer || formData.rangeAnswer.trim() === "") {
        newErrors.rangeAnswer = "Range answer is required";
      }
    }

    // Hindi question is now optional - no validation needed

    setErrors(newErrors);

    const errorCount = Object.values(newErrors).filter(Boolean).length;
    if (errorCount > 0) {
      // Required fields live on the English tab — switch there so errors are visible
      setCurrentLanguage("english");
      showWarning(
        errorCount === 1
          ? Object.values(newErrors).find(Boolean)
          : `${errorCount} fields need your attention. They're highlighted in the form.`,
        "Please complete the question"
      );
      setTimeout(() => {
        const firstError = document.querySelector(".aqn-container .error, .aqn-match-error");
        if (firstError) firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 350);
    }
    return errorCount === 0;
  };

  const handleSubmit = async (action = "save") => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();

      // Add required fields according to Laravel API specification
      formDataToSend.append("section_id", sectionId);

      // Optional topic name
      if (formData.topic_name.trim()) {
        formDataToSend.append("topic_name", formData.topic_name);
      }
      if (formData.topic_name_hi.trim()) {
        formDataToSend.append("topic_name_hi", formData.topic_name_hi.trim());
      }

      // Question type
      formDataToSend.append("question_type", formData.questionType);

      // Paragraph fields for PARAGRAPH type questions
      if (formData.questionType === "PARAGRAPH") {
        if (formData.paragraphEng && formData.paragraphEng.trim()) {
          formDataToSend.append("paragraph_en", formData.paragraphEng);
        }
        if (formData.paragraphHindi && formData.paragraphHindi.trim()) {
          formDataToSend.append("paragraph_hi", formData.paragraphHindi);
        }
        if (formData.paragraphEngImage) {
          formDataToSend.append("paragraph_en_image", formData.paragraphEngImage);
        }
        if (formData.paragraphHindiImage) {
          formDataToSend.append("paragraph_hi_image", formData.paragraphHindiImage);
        }
      }

      // English question
      formDataToSend.append("question_en", formData.questionEng);

      // Hindi question
      if (formData.questionHindi.trim()) {
        formDataToSend.append("question_hi", formData.questionHindi);
      }

      // Marks
      formDataToSend.append("marks", formData.marks);
      if (formData.negativeMarks) {
        formDataToSend.append("negative_marks", formData.negativeMarks);
      }

      // Handle different question types
      if (formData.questionType === "SINGLE_CORRECT" || formData.questionType === "PARAGRAPH") {
        // Correct answer (A, B, C, D, E, or F)
        formDataToSend.append("correct_answer", formData.correctAnswer);

        // Options array structure (dynamic based on optionCount)
        const optionKeys = ['A', 'B', 'C', 'D', 'E', 'F'];
        for (let i = 0; i < optionCount; i++) {
          const key = optionKeys[i];
          const optionEng = formData[`option${key}Eng`];
          const optionHindi = formData[`option${key}Hindi`];
          
          // Append option text fields
          formDataToSend.append(`options[${i}][key]`, key);
          formDataToSend.append(`options[${i}][option_en]`, optionEng);
          if (optionHindi && optionHindi.trim()) {
            formDataToSend.append(`options[${i}][option_hi]`, optionHindi);
          }
          
          // Append option images if exist
          const optionEngImage = formData[`option${key}EngImage`];
          if (optionEngImage) {
            formDataToSend.append(`options[${i}][option_en_image]`, optionEngImage);
          }
          const optionHindiImage = formData[`option${key}HindiImage`];
          if (optionHindiImage) {
            formDataToSend.append(`options[${i}][option_hi_image]`, optionHindiImage);
          }
        }
      } else if (formData.questionType === "MULTI_CORRECT") {
        // Correct answers as comma-separated string (e.g., "A,B,C")
        formDataToSend.append("correct_answer", formData.correctAnswers.join(","));

        // Options array structure (dynamic based on optionCount)
        const optionKeys = ['A', 'B', 'C', 'D', 'E', 'F'];
        for (let i = 0; i < optionCount; i++) {
          const key = optionKeys[i];
          const optionEng = formData[`option${key}Eng`];
          const optionHindi = formData[`option${key}Hindi`];
          
          // Append option text fields
          formDataToSend.append(`options[${i}][key]`, key);
          formDataToSend.append(`options[${i}][option_en]`, optionEng);
          if (optionHindi && optionHindi.trim()) {
            formDataToSend.append(`options[${i}][option_hi]`, optionHindi);
          }
          
          // Append option images if exist
          const optionEngImage = formData[`option${key}EngImage`];
          if (optionEngImage) {
            formDataToSend.append(`options[${i}][option_en_image]`, optionEngImage);
          }
          const optionHindiImage = formData[`option${key}HindiImage`];
          if (optionHindiImage) {
            formDataToSend.append(`options[${i}][option_hi_image]`, optionHindiImage);
          }
        }
      } else if (formData.questionType === "MATCH") {
        // Match the following type
        // Column I options (A, B, C, D, E, F) and Column II options (P, Q, R, S, T, U)
        const columnIKeys = ['A', 'B', 'C', 'D', 'E', 'F'];
        const columnIIKeys = ['P', 'Q', 'R', 'S', 'T', 'U'];
        
        // Add Column I options
        for (let i = 0; i < optionCount; i++) {
          const key = columnIKeys[i];
          const optionEng = formData[`option${key}Eng`];
          const optionHindi = formData[`option${key}Hindi`];
          
          formDataToSend.append(`options[${i}][key]`, key);
          formDataToSend.append(`options[${i}][option_en]`, optionEng);
          if (optionHindi && optionHindi.trim()) {
            formDataToSend.append(`options[${i}][option_hi]`, optionHindi);
          }
          
          // Append option images if exist
          const optionEngImage = formData[`option${key}EngImage`];
          if (optionEngImage) {
            formDataToSend.append(`options[${i}][option_en_image]`, optionEngImage);
          }
          const optionHindiImage = formData[`option${key}HindiImage`];
          if (optionHindiImage) {
            formDataToSend.append(`options[${i}][option_hi_image]`, optionHindiImage);
          }
        }
        
        // Add Column II options with match_key
        for (let i = 0; i < optionCount; i++) {
          const key = columnIIKeys[i];
          const optionEng = formData[`option${key}Eng`];
          const optionHindi = formData[`option${key}Hindi`];
          const matchKey = matchMappings[key] || ''; // Get the mapping from state
          
          const index = optionCount + i; // Continue from where Column I ended
          formDataToSend.append(`options[${index}][key]`, key);
          formDataToSend.append(`options[${index}][option_en]`, optionEng);
          if (optionHindi && optionHindi.trim()) {
            formDataToSend.append(`options[${index}][option_hi]`, optionHindi);
          }
          if (matchKey) {
            formDataToSend.append(`options[${index}][match_key]`, matchKey);
          }
          
          // Append option images if exist
          const optionEngImage = formData[`option${key}EngImage`];
          if (optionEngImage) {
            formDataToSend.append(`options[${index}][option_en_image]`, optionEngImage);
          }
          const optionHindiImage = formData[`option${key}HindiImage`];
          if (optionHindiImage) {
            formDataToSend.append(`options[${index}][option_hi_image]`, optionHindiImage);
          }
        }
      } else if (formData.questionType === "NUMERICAL") {
        // Numerical answer
        formDataToSend.append("numerical_value", formData.numericalValue);
        
        // Optional numerical ranges (if your API supports it)
        if (formData.numericalRanges && formData.numericalRanges.length > 0) {
          formData.numericalRanges.forEach((range, index) => {
            formDataToSend.append(`numerical_ranges[${index}]`, range);
          });
        }
      } else if (formData.questionType === "RANGE") {
        // Range type answer
        formDataToSend.append("correct_answer", formData.rangeAnswer);
      }

      // Optional explanations
      if (formData.explanationEng.trim()) {
        formDataToSend.append("explanation_en", formData.explanationEng);
      }
      if (formData.explanationHindi.trim()) {
        formDataToSend.append("explanation_hi", formData.explanationHindi);
      }

      // Optional YouTube solution link
      if (formData.youtubeLink && formData.youtubeLink.trim()) {
        formDataToSend.append("youtube_link", formData.youtubeLink.trim());
      }

      // Optional image
      if (formData.questionImage) {
        formDataToSend.append("image", formData.questionImage);
      }

      // Submit to API with proper FormData handling
      const response = await authFetch("/questions", {
        method: "POST",
        body: formDataToSend, // browser sets the multipart boundary
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Refetch questions to update the count
        const updatedResponse = await authFetch(`/questions/section/${sectionId}`);

        const updatedData = await updatedResponse.json();
        if (updatedResponse.ok && updatedData.success) {
          setQuestions(updatedData.data || []);
        }

        if (action === "saveAndNext") {
          // Reset form for next question
          setFormData({
            questionType: formData.questionType, // Keep the same question type
            paragraphEng: "",
            paragraphHindi: "",
            questionEng: "",
            optionAEng: "",
            optionBEng: "",
            optionCEng: "",
            optionDEng: "",
            optionEEng: "",
            optionFEng: "",
            optionPEng: "",
            optionQEng: "",
            optionREng: "",
            optionSEng: "",
            optionTEng: "",
            optionUEng: "",
            explanationEng: "",
            questionHindi: "",
            optionAHindi: "",
            optionBHindi: "",
            optionCHindi: "",
            optionDHindi: "",
            optionEHindi: "",
            optionFHindi: "",
            optionPHindi: "",
            optionQHindi: "",
            optionRHindi: "",
            optionSHindi: "",
            optionTHindi: "",
            optionUHindi: "",
            explanationHindi: "",
            paragraphEngImage: null,
            paragraphHindiImage: null,
            paragraphEngImagePreview: null,
            paragraphHindiImagePreview: null,
            correctAnswer: "A",
            correctAnswers: [],
            numericalValue: "",
            numericalRanges: [],
            rangeAnswer: "",
            marks: formData.marks, // Keep the same marks
            negativeMarks: formData.negativeMarks, // Keep the same negative marks
            topic_name: "",
            topic_name_hi: "",
            youtubeLink: "",
            questionImage: null,
            imagePreview: null,
            // Reset option images
            optionAEngImage: null,
            optionBEngImage: null,
            optionCEngImage: null,
            optionDEngImage: null,
            optionEEngImage: null,
            optionFEngImage: null,
            optionPEngImage: null,
            optionQEngImage: null,
            optionREngImage: null,
            optionSEngImage: null,
            optionTEngImage: null,
            optionUEngImage: null,
            optionAHindiImage: null,
            optionBHindiImage: null,
            optionCHindiImage: null,
            optionDHindiImage: null,
            optionEHindiImage: null,
            optionFHindiImage: null,
            optionPHindiImage: null,
            optionQHindiImage: null,
            optionRHindiImage: null,
            optionSHindiImage: null,
            optionTHindiImage: null,
            optionUHindiImage: null,
            optionAEngImagePreview: null,
            optionBEngImagePreview: null,
            optionCEngImagePreview: null,
            optionDEngImagePreview: null,
            optionEEngImagePreview: null,
            optionFEngImagePreview: null,
            optionPEngImagePreview: null,
            optionQEngImagePreview: null,
            optionREngImagePreview: null,
            optionSEngImagePreview: null,
            optionTEngImagePreview: null,
            optionUEngImagePreview: null,
            optionAHindiImagePreview: null,
            optionBHindiImagePreview: null,
            optionCHindiImagePreview: null,
            optionDHindiImagePreview: null,
            optionEHindiImagePreview: null,
            optionFHindiImagePreview: null,
            optionPHindiImagePreview: null,
            optionQHindiImagePreview: null,
            optionRHindiImagePreview: null,
            optionSHindiImagePreview: null,
            optionTHindiImagePreview: null,
            optionUHindiImagePreview: null,
          });
          setMatchMappings({});
          setErrors({});
          showSuccess(
            `${(updatedData.data || []).length} question(s) in this section now. The form is ready for the next one.`,
            "Question saved"
          );
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          showSuccess(
            `${(updatedData.data || []).length} question(s) in this section now.`,
            "Question saved"
          );
          navigate(`/admin/test-series/${seriesId}/section/${sectionId}/questions`);
        }
      } else {
        throw new Error(firstApiError(data, `Failed to save question (HTTP ${response.status})`));
      }
    } catch (error) {
      showError(error.message, "Couldn't save the question");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderFieldWithPreview = (fieldName, label, rows = 3) => {
    const value = formData[fieldName] || "";
    const error = errors[fieldName];

    return (
      <div className="aqn-form-group">
        <label className="aqn-label">{label}</label>
        <textarea
          ref={(el) => (textareaRefs.current[fieldName] = el)}
          name={fieldName}
          value={value}
          onChange={handleChange}
          onFocus={(e) => (lastFocusedTextarea.current = e.target)}
          rows={rows}
          className={`aqn-textarea${error ? " error" : ""}`}
          placeholder={`Enter ${label.toLowerCase()}... Use \\( \\) for equations`}
        />
        {error && <span className="aqn-error-msg">{error}</span>}
        <MathPreview text={value} />
      </div>
    );
  };

  // Render option field with image upload
  const renderOptionWithImage = (optionKey, label, rows = 2, displayKey = optionKey) => {
    const lang = currentLanguage === "english" ? "en" : "hi";
    const suffix = currentLanguage === "english" ? "Eng" : "Hindi";
    const textFieldName = `option${optionKey}${suffix}`;
    const value = formData[textFieldName] || "";
    const error = errors[textFieldName];
    const imagePreview = formData[`option${optionKey}${suffix}ImagePreview`];

    return (
      <div className="aqn-option-card" key={optionKey}>
        <span className="aqn-option-card-label">{displayKey}</span>

        {/* Text field */}
        <div className="aqn-option-text-col">
          <label className="aqn-label">{label}</label>
          <textarea
            ref={(el) => (textareaRefs.current[textFieldName] = el)}
            name={textFieldName}
            value={value}
            onChange={handleChange}
            onFocus={(e) => (lastFocusedTextarea.current = e.target)}
            rows={rows}
            className={`aqn-textarea${error ? " error" : ""}`}
            placeholder={`Enter ${label.toLowerCase()}... Use \\( \\) for equations`}
          />
          {error && <span className="aqn-error-msg">{error}</span>}
          {value && <MathPreview text={value} />}
        </div>

        {/* Image upload */}
        <div className="aqn-option-image-col">
          <span className="aqn-option-image-hint">
            Optional image ({lang.toUpperCase()}) · max 250 KB
          </span>
          {imagePreview ? (
            <div className="aqn-image-preview-wrap">
              <img src={imagePreview} alt={`${label} preview`} />
              <button
                type="button"
                className="aqn-image-remove-btn"
                onClick={() => removeOptionImage(optionKey, lang)}
                title="Remove image"
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
          ) : (
            <input
              type="file"
              accept="image/*"
              className="aqn-file-input"
              onChange={(e) => handleOptionImageUpload(optionKey, lang, e)}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="aqn-container">
      {/* Full-page saving overlay */}
      {isSubmitting && (
        <div className="aqn-saving-overlay">
          <div className="aqn-saving-spinner" />
          <span className="aqn-saving-text">Saving question...</span>
        </div>
      )}

      {/* Page Header */}
      <div className="page-header">
        <div>
          <button type="button" className="back-link" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-left"></i> Back
          </button>
          <h2>Add question</h2>
          <p>Write the question in English (required) and Hindi (optional). Use the symbol palette for maths, physics and chemistry.</p>
          <div className="aqn-header-meta">
            {sectionName && (
              <span className="badge badge-brand">
                <i className="bi bi-layers"></i> {sectionName}
              </span>
            )}
            {isLoading ? (
              <span className="badge badge-neutral">Loading existing questions…</span>
            ) : (
              <span
                className={`badge ${targetCount && questions.length >= targetCount ? "badge-warning" : "badge-neutral"}`}
                title={
                  targetCount && questions.length >= targetCount
                    ? "The section's target is reached. You can still add more; students will see every question."
                    : undefined
                }
              >
                {questions.length}
                {targetCount ? ` of ${targetCount}` : ""} question{(targetCount || questions.length) === 1 ? "" : "s"} in this section
                {targetCount > 0 && questions.length >= targetCount && " · target reached"}
              </span>
            )}
          </div>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(`/admin/test-series/${seriesId}/section/${sectionId}/questions`)}
          >
            <i className="bi bi-list-ul"></i> View questions
          </button>
        </div>
      </div>

      {/* Maths / physics / chemistry symbol palette */}
      <SymbolToolbar onInsert={insertEquation} sectionName={sectionName} />

      {/* Language & math wrap controls */}
      <div className="aqn-controls">
        <div className="aqn-lang-switcher">
          <button
            type="button"
            className={`aqn-lang-btn${currentLanguage === "english" ? " active" : ""}`}
            onClick={() => setCurrentLanguage("english")}
          >
            English
          </button>
          <button
            type="button"
            className={`aqn-lang-btn${currentLanguage === "hindi" ? " active" : ""}`}
            onClick={() => setCurrentLanguage("hindi")}
          >
            हिंदी
          </button>
        </div>
        <button
          type="button"
          className="aqn-math-wrap-btn"
          onClick={() => wrapWithMath("inline")}
          title="Wrap selected text with inline math \( \)"
        >
          <i className="bi bi-braces"></i> Inline math
        </button>
        <button
          type="button"
          className="aqn-math-wrap-btn"
          onClick={() => wrapWithMath("display")}
          title="Wrap selected text with display math $$ $$"
        >
          <i className="bi bi-textarea-t"></i> Display math
        </button>
      </div>

      {/* Form Card */}
      <div className="aqn-form-card">
        {/* Question Type */}
        <div className="aqn-form-group">
          <label className="aqn-label">Question Type *</label>
          <select
            name="questionType"
            value={formData.questionType}
            onChange={handleChange}
            className={`aqn-select${errors.questionType ? " error" : ""}`}
          >
            <option value="SINGLE_CORRECT">Single Correct Answer (MCQ)</option>
            <option value="MULTI_CORRECT">Multiple Correct Answers</option>
            <option value="NUMERICAL">Numerical Type</option>
            <option value="MATCH">Match the Following</option>
            <option value="PARAGRAPH">Paragraph/Comprehension Type</option>
            <option value="RANGE">Range Type Questions</option>
          </select>
          {errors.questionType && <span className="aqn-error-msg">{errors.questionType}</span>}
        </div>

        {/* Marks Fields */}
        <div className="aqn-form-row">
          <div className="aqn-form-group">
            <label className="aqn-label">Positive Marks *</label>
            <input
              type="number"
              name="marks"
              value={formData.marks}
              onChange={handleChange}
              min="0"
              step="0.25"
              placeholder="e.g., 4"
              className={`aqn-input${errors.marks ? " error" : ""}`}
            />
            {errors.marks && <span className="aqn-error-msg">{errors.marks}</span>}
          </div>
          <div className="aqn-form-group">
            <label className="aqn-label">Negative Marks <span className="aqn-label-optional">(optional)</span></label>
            <input
              type="number"
              name="negativeMarks"
              value={formData.negativeMarks}
              onChange={handleChange}
              min="0"
              step="0.25"
              placeholder="e.g., 1"
              className="aqn-input"
            />
          </div>
        </div>

        {currentLanguage === "english" ? (
          <>
            {formData.questionType === "PARAGRAPH" && (
              <>
                {renderFieldWithPreview("paragraphEng", "Paragraph/Comprehension Text (English)", 6)}
                <div className="aqn-form-group">
                  <label className="aqn-label">Paragraph Image (English) <span className="aqn-label-optional">optional · max 250 KB</span></label>
                  {formData.paragraphEngImagePreview ? (
                    <div className="aqn-para-image-preview">
                      <img src={formData.paragraphEngImagePreview} alt="Paragraph English preview" />
                      <button
                        type="button"
                        onClick={() => removeParagraphImage("en")}
                        className="aqn-btn aqn-btn-danger aqn-btn-sm"
                      >
                        <i className="bi bi-trash3"></i> Remove
                      </button>
                    </div>
                  ) : (
                    <input
                      type="file"
                      accept="image/*"
                      className="aqn-file-input"
                      onChange={(e) => handleParagraphImageUpload("en", e)}
                    />
                  )}
                </div>
              </>
            )}

            {renderFieldWithPreview("questionEng", "Question (English)", 4)}
            
            {/* Conditional Rendering Based on Question Type */}
            {formData.questionType === "MATCH" ? (
              <>
                {/* Match the Following - Two Columns */}
                <div>
                  <h3 className="aqn-section-title">Column I (Items to Match)</h3>
                  <div className="aqn-option-control">
                    <span className="aqn-option-control-label">Number of Items in Column I (4–6)</span>
                    <button
                      type="button"
                      onClick={() => setOptionCount(Math.max(4, optionCount - 1))}
                      disabled={optionCount <= 4}
                      className="aqn-btn aqn-btn-secondary aqn-btn-sm"
                    >
                      − Remove
                    </button>
                    <span className="aqn-option-count-display">
                      {optionCount} Items ({matchLabel("A")}–{matchLabel(MATCH_COLUMN_I_KEYS[optionCount - 1])})
                    </span>
                    <button
                      type="button"
                      onClick={() => setOptionCount(Math.min(6, optionCount + 1))}
                      disabled={optionCount >= 6}
                      className="aqn-btn aqn-btn-secondary aqn-btn-sm"
                    >
                      + Add
                    </button>
                  </div>
                  {Array.from({length: optionCount}, (_, i) => {
                    const key = String.fromCharCode(65 + i); // A, B, C, D, E, F
                    return renderOptionWithImage(key, `Column I - Item ${matchLabel(key)}`, 2, matchLabel(key));
                  })}
                </div>

                <div>
                  <h3 className="aqn-section-title">Column II (Options to Match With)</h3>
                  <div className="aqn-match-tip">
                    <i className="bi bi-lightbulb"></i> <strong>Tip:</strong> Use the dropdown on the right of each Column II item to select which Column I item it matches with. Each Column I item can only be used once.
                  </div>
                  {Array.from({length: optionCount}, (_, i) => {
                    const key = ['P', 'Q', 'R', 'S', 'T', 'U'][i]; // P, Q, R, S, T, U
                    const columnIKeys = Array.from({length: optionCount}, (_, j) => String.fromCharCode(65 + j));
                    
                    // Get already used Column I items (excluding current selection)
                    const usedColumnIKeys = Object.entries(matchMappings)
                      .filter(([colIIKey, colIKey]) => colIIKey !== key && colIKey)
                      .map(([_, colIKey]) => colIKey);
                    
                    // Available options: current selection + unused items
                    const availableColumnIKeys = columnIKeys.filter(
                      colIKey => !usedColumnIKeys.includes(colIKey)
                    );
                    
                    return (
                      <div key={key} className="aqn-match-row">
                        <div className="aqn-match-row-option">
                          {renderOptionWithImage(key, `Column II - Item ${matchLabel(key)}`, 2, matchLabel(key))}
                        </div>
                        <div className="aqn-match-dropdown-col">
                          <label className="aqn-label">Matches with:</label>
                          <select
                            value={matchMappings[key] || ""}
                            className={`aqn-match-select${matchMappings[key] ? " mapped" : ""}`}
                            onChange={(e) => {
                              setMatchMappings({
                                ...matchMappings,
                                [key]: e.target.value
                              });
                              if (errors.matchMappings) {
                                setErrors({ ...errors, matchMappings: null });
                              }
                            }}
                          >
                            <option value="">Select...</option>
                            {availableColumnIKeys.map(colIKey => (
                              <option key={colIKey} value={colIKey}>
                                Item {matchLabel(colIKey)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                  {errors.matchMappings && (
                    <div className="aqn-match-error">{errors.matchMappings}</div>
                  )}
                  {Object.keys(matchMappings).length > 0 && (
                    <div className="aqn-match-mappings">
                      <strong>Current Mappings:</strong>
                      <div className="aqn-match-mapping-chips">
                        {Object.entries(matchMappings)
                          .filter(([, colI]) => colI)
                          .sort(([, a], [, b]) => a.localeCompare(b))
                          .map(([colII, colI]) => (
                            <span key={colII} className="aqn-match-chip">{matchLabel(colI)} → {matchLabel(colII)}</span>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (formData.questionType === "SINGLE_CORRECT" || 
              formData.questionType === "MULTI_CORRECT" || 
              formData.questionType === "PARAGRAPH") && (
              <>
                {/* Dynamic Options Controls */}
                <div className="aqn-option-control">
                  <span className="aqn-option-control-label">Number of Options (4–6)</span>
                  <button
                    type="button"
                    onClick={() => setOptionCount(Math.max(4, optionCount - 1))}
                    disabled={optionCount <= 4}
                    className="aqn-btn aqn-btn-secondary aqn-btn-sm"
                  >
                    − Remove
                  </button>
                  <span className="aqn-option-count-display">
                    {optionCount} Options (A–{String.fromCharCode(64 + optionCount)})
                  </span>
                  <button
                    type="button"
                    onClick={() => setOptionCount(Math.min(6, optionCount + 1))}
                    disabled={optionCount >= 6}
                    className="aqn-btn aqn-btn-secondary aqn-btn-sm"
                  >
                    + Add
                  </button>
                </div>

                {/* Dynamic Option Fields with Images */}
                {Array.from({length: optionCount}, (_, i) => {
                  const key = String.fromCharCode(65 + i); // A, B, C, D, E, F
                  return renderOptionWithImage(key, `Option ${key}`, 2);
                })}
              </>
            )}

            {formData.questionType === "NUMERICAL" && (
              <div className="aqn-form-group">
                <label className="aqn-label">Numerical Answer Value *</label>
                <input
                  type="number"
                  name="numericalValue"
                  value={formData.numericalValue}
                  onChange={handleChange}
                  step="any"
                  placeholder="e.g., 3.14"
                  className={`aqn-input${errors.numericalValue ? " error" : ""}`}
                />
                {errors.numericalValue && <span className="aqn-error-msg">{errors.numericalValue}</span>}
                <span className="aqn-hint">Enter the correct numerical answer</span>
              </div>
            )}

            {formData.questionType === "RANGE" && (
              <div className="aqn-form-group">
                <label className="aqn-label">Range Answer *</label>
                <input
                  type="text"
                  name="rangeAnswer"
                  value={formData.rangeAnswer}
                  onChange={handleChange}
                  placeholder="e.g., 2-5 or 3.5"
                  className={`aqn-input${errors.rangeAnswer ? " error" : ""}`}
                />
                {errors.rangeAnswer && <span className="aqn-error-msg">{errors.rangeAnswer}</span>}
                <span className="aqn-hint">Enter the correct range answer (e.g., 2-5)</span>
              </div>
            )}

            {renderFieldWithPreview(
              "explanationEng",
              "Explanation (English)",
              5
            )}
          </>
        ) : (
          <>
            {formData.questionType === "PARAGRAPH" && (
              <>
                {renderFieldWithPreview("paragraphHindi", "पैराग्राफ/बोधगम्यता पाठ (हिंदी)", 6)}
                <div className="aqn-form-group">
                  <label className="aqn-label">Paragraph Image (Hindi) <span className="aqn-label-optional">optional · max 250 KB</span></label>
                  {formData.paragraphHindiImagePreview ? (
                    <div className="aqn-para-image-preview">
                      <img src={formData.paragraphHindiImagePreview} alt="Paragraph Hindi preview" />
                      <button
                        type="button"
                        onClick={() => removeParagraphImage("hi")}
                        className="aqn-btn aqn-btn-danger aqn-btn-sm"
                      >
                        <i className="bi bi-trash3"></i> Remove
                      </button>
                    </div>
                  ) : (
                    <input
                      type="file"
                      accept="image/*"
                      className="aqn-file-input"
                      onChange={(e) => handleParagraphImageUpload("hi", e)}
                    />
                  )}
                </div>
              </>
            )}

            {renderFieldWithPreview("questionHindi", "प्रश्न (हिंदी)", 4)}
            
            {/* Conditional Rendering Based on Question Type */}
            {formData.questionType === "MATCH" ? (
              <>
                {/* Match the Following - Two Columns */}
                <div>
                  <h3 className="aqn-section-title">कॉलम I (मिलान करने के लिए आइटम)</h3>
                  <div className="aqn-option-control">
                    <span className="aqn-option-control-label">कॉलम I में आइटम की संख्या (4–6)</span>
                    <button
                      type="button"
                      onClick={() => setOptionCount(Math.max(4, optionCount - 1))}
                      disabled={optionCount <= 4}
                      className="aqn-btn aqn-btn-secondary aqn-btn-sm"
                    >
                      − हटाएं
                    </button>
                    <span className="aqn-option-count-display">
                      {optionCount} आइटम ({matchLabel("A")}–{matchLabel(MATCH_COLUMN_I_KEYS[optionCount - 1])})
                    </span>
                    <button
                      type="button"
                      onClick={() => setOptionCount(Math.min(6, optionCount + 1))}
                      disabled={optionCount >= 6}
                      className="aqn-btn aqn-btn-secondary aqn-btn-sm"
                    >
                      + जोड़ें
                    </button>
                  </div>
                  {Array.from({length: optionCount}, (_, i) => {
                    const key = String.fromCharCode(65 + i); // A, B, C, D, E, F
                    return renderOptionWithImage(key, `कॉलम I - आइटम ${matchLabel(key)}`, 2, matchLabel(key));
                  })}
                </div>

                <div>
                  <h3 className="aqn-section-title">कॉलम II (मिलान करने के विकल्प)</h3>
                  <div className="aqn-match-tip">
                    <i className="bi bi-lightbulb"></i> <strong>सुझाव:</strong> प्रत्येक कॉलम II आइटम के दाईं ओर ड्रॉपडाउन का उपयोग करके चुनें कि यह कॉलम I के किस आइटम से मेल खाता है।
                  </div>
                  {Array.from({length: optionCount}, (_, i) => {
                    const key = ['P', 'Q', 'R', 'S', 'T', 'U'][i]; // P, Q, R, S, T, U
                    const columnIKeys = Array.from({length: optionCount}, (_, j) => String.fromCharCode(65 + j));
                    
                    // Get already used Column I items (excluding current selection)
                    const usedColumnIKeys = Object.entries(matchMappings)
                      .filter(([colIIKey, colIKey]) => colIIKey !== key && colIKey)
                      .map(([_, colIKey]) => colIKey);
                    
                    // Available options: current selection + unused items
                    const availableColumnIKeys = columnIKeys.filter(
                      colIKey => !usedColumnIKeys.includes(colIKey)
                    );
                    
                    return (
                      <div key={key} className="aqn-match-row">
                        <div className="aqn-match-row-option">
                          {renderOptionWithImage(key, `कॉलम II - आइटम ${matchLabel(key)}`, 2, matchLabel(key))}
                        </div>
                        <div className="aqn-match-dropdown-col">
                          <label className="aqn-label">मेल खाता है:</label>
                          <select
                            value={matchMappings[key] || ""}
                            className={`aqn-match-select${matchMappings[key] ? " mapped" : ""}`}
                            onChange={(e) => {
                              setMatchMappings({
                                ...matchMappings,
                                [key]: e.target.value
                              });
                              if (errors.matchMappings) {
                                setErrors({ ...errors, matchMappings: null });
                              }
                            }}
                          >
                            <option value="">चुनें...</option>
                            {availableColumnIKeys.map(colIKey => (
                              <option key={colIKey} value={colIKey}>
                                आइटम {matchLabel(colIKey)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                  {errors.matchMappings && (
                    <div className="aqn-match-error">{errors.matchMappings}</div>
                  )}
                  {Object.keys(matchMappings).length > 0 && (
                    <div className="aqn-match-mappings">
                      <strong>वर्तमान मिलान:</strong>
                      <div className="aqn-match-mapping-chips">
                        {Object.entries(matchMappings)
                          .filter(([, colI]) => colI)
                          .sort(([, a], [, b]) => a.localeCompare(b))
                          .map(([colII, colI]) => (
                            <span key={colII} className="aqn-match-chip">{matchLabel(colI)} → {matchLabel(colII)}</span>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (formData.questionType === "SINGLE_CORRECT" || 
              formData.questionType === "MULTI_CORRECT" || 
              formData.questionType === "PARAGRAPH") && (
              <>
                {/* Dynamic Options Controls */}
                <div className="aqn-option-control">
                  <span className="aqn-option-control-label">विकल्पों की संख्या (4–6)</span>
                  <button
                    type="button"
                    onClick={() => setOptionCount(Math.max(4, optionCount - 1))}
                    disabled={optionCount <= 4}
                    className="aqn-btn aqn-btn-secondary aqn-btn-sm"
                  >
                    − हटाएं
                  </button>
                  <span className="aqn-option-count-display">
                    {optionCount} विकल्प (A–{String.fromCharCode(64 + optionCount)})
                  </span>
                  <button
                    type="button"
                    onClick={() => setOptionCount(Math.min(6, optionCount + 1))}
                    disabled={optionCount >= 6}
                    className="aqn-btn aqn-btn-secondary aqn-btn-sm"
                  >
                    + जोड़ें
                  </button>
                </div>

                {/* Dynamic Option Fields with Images */}
                {Array.from({length: optionCount}, (_, i) => {
                  const key = String.fromCharCode(65 + i); // A, B, C, D, E, F
                  return renderOptionWithImage(key, `विकल्प ${key}`, 2);
                })}
              </>
            )}

            {formData.questionType === "NUMERICAL" && (
              <div className="aqn-form-group">
                <label className="aqn-label">संख्यात्मक उत्तर मान *</label>
                <input
                  type="number"
                  name="numericalValue"
                  value={formData.numericalValue}
                  onChange={handleChange}
                  step="any"
                  placeholder="उदा., 3.14"
                  className={`aqn-input${errors.numericalValue ? " error" : ""}`}
                />
                {errors.numericalValue && <span className="aqn-error-msg">{errors.numericalValue}</span>}
                <span className="aqn-hint">सही संख्यात्मक उत्तर दर्ज करें</span>
              </div>
            )}

            {formData.questionType === "RANGE" && (
              <div className="aqn-form-group">
                <label className="aqn-label">रेंज उत्तर *</label>
                <input
                  type="text"
                  name="rangeAnswer"
                  value={formData.rangeAnswer}
                  onChange={handleChange}
                  placeholder="उदा., 2-5 या 3.5"
                  className={`aqn-input${errors.rangeAnswer ? " error" : ""}`}
                />
                {errors.rangeAnswer && <span className="aqn-error-msg">{errors.rangeAnswer}</span>}
                <span className="aqn-hint">सही रेंज उत्तर दर्ज करें (उदा., 2-5)</span>
              </div>
            )}

            {renderFieldWithPreview("explanationHindi", "व्याख्या (हिंदी)", 5)}
          </>
        )}

        {/* Common Fields */}
        {formData.questionType === "SINGLE_CORRECT" && (
          <div className="aqn-form-group">
            <label className="aqn-label">Correct Answer *</label>
            <select
              name="correctAnswer"
              value={formData.correctAnswer}
              onChange={handleChange}
              className={`aqn-select${errors.correctAnswer ? " error" : ""}`}
            >
              {Array.from({length: optionCount}, (_, i) => {
                const key = String.fromCharCode(65 + i);
                return <option key={key} value={key}>{key}</option>;
              })}
            </select>
            {errors.correctAnswer && <span className="aqn-error-msg">{errors.correctAnswer}</span>}
          </div>
        )}

        {formData.questionType === "MULTI_CORRECT" && (
          <div className="aqn-form-group">
            <label className="aqn-label">Correct Answers * (Select all that apply)</label>
            <div className="aqn-checkbox-panel">
              <p className="aqn-checkbox-hint">Select all correct options:</p>
              <p className="aqn-checkbox-hint">
                Partial marking (JEE Advanced): all correct options chosen = full marks; only some correct
                options chosen and no wrong one = +1 per option on a 4-mark question (marks ÷ number of
                options each); any wrong option = negative marks.
              </p>
              {Array.from({length: optionCount}, (_, i) => {
                const key = String.fromCharCode(65 + i);
                return (
                  <label key={key} className="aqn-checkbox-label">
                    <input
                      type="checkbox"
                      className="aqn-checkbox-input"
                      checked={formData.correctAnswers.includes(key)}
                      onChange={(e) => {
                        const newCorrectAnswers = e.target.checked
                          ? [...formData.correctAnswers, key]
                          : formData.correctAnswers.filter((a) => a !== key);
                        setFormData({ ...formData, correctAnswers: newCorrectAnswers });
                      }}
                    />
                    <span className="aqn-checkbox-text">Option {key}</span>
                  </label>
                );
              })}
              {formData.correctAnswers.length > 0 && (
                <div className="aqn-selected-answers-tag">
                  Selected: {formData.correctAnswers.join(", ")}
                </div>
              )}
            </div>
            {errors.correctAnswers && <span className="aqn-error-msg">{errors.correctAnswers}</span>}
          </div>
        )}



        {formData.questionType === "PARAGRAPH" && (
          <div className="aqn-form-group">
            <label className="aqn-label">Correct Answer *</label>
            <select
              name="correctAnswer"
              value={formData.correctAnswer}
              onChange={handleChange}
              className={`aqn-select${errors.correctAnswer ? " error" : ""}`}
            >
              {Array.from({length: optionCount}, (_, i) => {
                const key = String.fromCharCode(65 + i);
                return <option key={key} value={key}>{key}</option>;
              })}
            </select>
            {errors.correctAnswer && <span className="aqn-error-msg">{errors.correctAnswer}</span>}
          </div>
        )}

        <div className="aqn-form-group">
          <label className="aqn-label">Topic Name <span className="aqn-label-optional">(optional)</span></label>
          <input
            type="text"
            name="topic_name"
            value={formData.topic_name}
            onChange={handleChange}
            placeholder="e.g., Calculus, Organic Chemistry"
            className="aqn-input"
          />
        </div>

        <div className="aqn-form-group">
          <label className="aqn-label">Topic Name (Hindi) <span className="aqn-label-optional">(optional)</span></label>
          <input
            type="text"
            name="topic_name_hi"
            value={formData.topic_name_hi}
            onChange={handleChange}
            placeholder="e.g., कलन, कार्बनिक रसायन"
            maxLength={255}
            className="aqn-input"
          />
        </div>

        {/* YouTube Solution Link */}
        <div className="aqn-form-group">
          <label className="aqn-label">YouTube Solution Link <span className="aqn-label-optional">(optional)</span></label>
          <input
            type="url"
            name="youtubeLink"
            value={formData.youtubeLink}
            onChange={handleChange}
            placeholder="e.g., https://www.youtube.com/watch?v=..."
            className="aqn-input"
          />
          <span className="aqn-hint">Enter the YouTube video link for solution explanation</span>
        </div>

        {/* Question Image */}
        <div className="aqn-form-group">
          <label className="aqn-label">Question Image <span className="aqn-label-optional">(optional)</span></label>
          {formData.imagePreview ? (
            <div className="aqn-question-image-preview">
              <img src={formData.imagePreview} alt="Preview" />
              <button type="button" onClick={removeImage} className="aqn-btn aqn-btn-danger aqn-btn-sm">
                <i className="bi bi-trash3"></i> Remove
              </button>
            </div>
          ) : (
            <input type="file" accept="image/*" className="aqn-file-input" onChange={handleImageUpload} />
          )}
        </div>

        {/* Submit Buttons */}
        <div className="aqn-form-actions">
          <button
            type="button"
            onClick={() => handleSubmit("save")}
            disabled={isSubmitting}
            className="aqn-btn aqn-btn-primary"
          >
            {isSubmitting ? <span className="btn-spinner"></span> : <i className="bi bi-check2"></i>}
            Save question
          </button>
          <button
            type="button"
            onClick={() => handleSubmit("saveAndNext")}
            disabled={isSubmitting}
            className="aqn-btn aqn-btn-success"
          >
            <i className="bi bi-plus-lg"></i> Save &amp; add another
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddQuestionNew;
